import * as THREE from 'three';
import {
  BlendMode,
  MeshBasicNodeMaterial,
  MeshLambertNodeMaterial,
  MeshStandardNodeMaterial,
} from 'three/webgpu';
import {
  attribute,
  instancedBufferAttribute,
  mrt,
  normalLocal,
  output,
  positionLocal,
  storage,
  transformNormalToView,
  uint,
  varying,
  vec4,
} from 'three/tsl';
import { PhysicsEngine } from '../physics/PhysicsEngine';
import { registerGtaoPresentationOverlayMaterial } from './gtaoPresentationOverlay';

export type TrackedBodyVisualSet = {
  addBody: (body: number) => void;
  syncVisuals: (engine: PhysicsEngine) => void;
  dispose: () => void;
};

export function createTrackedBodyVisualSet(
  scene: THREE.Object3D,
  physics: PhysicsEngine,
  options: {
    capacity: number;
    halfExtents?: [number, number, number];
    geometry?: THREE.BufferGeometry;
    disposeGeometry?: boolean;
    color: number;
    roughness?: number;
    metalness?: number;
    materialModel?: 'standard' | 'lambert';
    shaderDrivenInstancing?: boolean;
    outlineScale?: number;
    showOutline?: boolean;
    castShadow?: boolean;
    receiveShadow?: boolean;
  },
): TrackedBodyVisualSet {
  const { capacity } = options;
  const outlineScale = options.outlineScale ?? 1.002;
  const showOutline = options.showOutline ?? true;
  const castShadow = options.castShadow ?? true;
  const receiveShadow = options.receiveShadow ?? true;
  const shaderDrivenInstancing = options.shaderDrivenInstancing ?? false;
  const ownsGeometry = options.geometry === undefined || options.disposeGeometry === true;
  const geometry = options.geometry
    ?? new THREE.BoxGeometry(
      (options.halfExtents?.[0] ?? 0.5) * 2.0,
      (options.halfExtents?.[1] ?? 0.5) * 2.0,
      (options.halfExtents?.[2] ?? 0.5) * 2.0,
    );

  const material = options.materialModel === 'lambert'
    ? new MeshLambertNodeMaterial()
    : new MeshStandardNodeMaterial();
  material.color.setHex(options.color);
  if (material instanceof MeshStandardNodeMaterial) {
    material.roughness = options.roughness ?? 0.65;
    material.metalness = options.metalness ?? 0.06;
  }

  // The default path remains an InstancedMesh for small/general scenes. Large
  // shader-driven sets only need a single u32 body id per instance; using a
  // plain Mesh avoids InstancedMesh's otherwise-unused 64-byte instanceMatrix.
  const bodyIndexData = shaderDrivenInstancing
    ? new Uint32Array(capacity)
    : new Float32Array(capacity * 4);
  const bodyIndexItemSize = shaderDrivenInstancing ? 1 : 4;
  const bodyIndexAttr = new THREE.InstancedBufferAttribute(
    bodyIndexData,
    bodyIndexItemSize,
    false,
  );
  const shaderGeometry = shaderDrivenInstancing
    ? new THREE.InstancedBufferGeometry().copy(geometry as THREE.InstancedBufferGeometry)
    : null;
  if (shaderGeometry) {
    shaderGeometry.setAttribute('bodyIndex', bodyIndexAttr);
    shaderGeometry.instanceCount = 0;
  }
  const renderGeometry = shaderGeometry ?? geometry;
  const bodyMesh: THREE.Mesh = shaderDrivenInstancing
    ? new THREE.Mesh(renderGeometry, material)
    : new THREE.InstancedMesh(renderGeometry, material, capacity);
  bodyMesh.castShadow = castShadow;
  bodyMesh.receiveShadow = receiveShadow;
  bodyMesh.frustumCulled = false;
  // Until PhysicsEngine publishes its GPU render buffers, every instance would
  // otherwise use the geometry's untransformed origin. Large scenes then show
  // a misleading single pixel/brick and wastefully overdraw tens of thousands
  // of coincident instances during async startup prewarm.
  bodyMesh.visible = false;
  if (!shaderDrivenInstancing) {
    (bodyMesh as THREE.InstancedMesh).count = 0;
  }
  scene.add(bodyMesh);

  const outlineMaterial = new MeshBasicNodeMaterial();
  outlineMaterial.color.setHex(0x111111);
  outlineMaterial.wireframe = true;
  outlineMaterial.transparent = true;
  outlineMaterial.opacity = 0.5;
  outlineMaterial.depthWrite = false;
  // Keep transparent outlines out of the GTAO geometry guide. The source
  // alpha is zero, so MaterialBlending preserves the opaque normal attachment.
  const outlineMrt = mrt({ output, normal: vec4(0, 0, 0, 0) });
  outlineMrt.setBlendMode('normal', new BlendMode(THREE.MaterialBlending));
  registerGtaoPresentationOverlayMaterial(outlineMaterial, outlineMrt);

  const outlineMesh: THREE.Mesh = shaderDrivenInstancing
    ? new THREE.Mesh(renderGeometry, outlineMaterial)
    : new THREE.InstancedMesh(renderGeometry, outlineMaterial, capacity);
  outlineMesh.frustumCulled = false;
  outlineMesh.visible = false;
  if (!shaderDrivenInstancing) {
    (outlineMesh as THREE.InstancedMesh).count = 0;
  }
  outlineMesh.renderOrder = 1;
  if (showOutline) {
    scene.add(outlineMesh);
  }

  let trackedCount = 0;
  let gpuVisualsBound = false;

  return {
    addBody(body: number): void {
      if (trackedCount >= capacity) {
        throw new Error(`Tracked body visual capacity exceeded (${capacity})`);
      }
      const attributeOffset = trackedCount * bodyIndexItemSize;
      bodyIndexData[attributeOffset] = body;
      // Initial scene construction happens before the attribute is bound, so
      // repeatedly bumping its version only creates avoidable CPU work. Runtime
      // appends (projectiles, spawning) still upload exactly the new element.
      if (gpuVisualsBound) {
        bodyIndexAttr.addUpdateRange(attributeOffset, bodyIndexItemSize);
        bodyIndexAttr.needsUpdate = true;
      }
      trackedCount++;
      if (shaderGeometry) {
        shaderGeometry.instanceCount = trackedCount;
      } else {
        (bodyMesh as THREE.InstancedMesh).count = trackedCount;
        if (showOutline) {
          (outlineMesh as THREE.InstancedMesh).count = trackedCount;
        }
      }
    },

    syncVisuals(engine: PhysicsEngine): void {
      if (gpuVisualsBound) return;

      const renderBuffers = engine.getRenderBuffers();
      if (!renderBuffers) return;

      const positions = storage(renderBuffers.positions, 'vec4', physics.config.maxBodies).toReadOnly();
      const quaternions = storage(renderBuffers.quaternions, 'vec4', physics.config.maxBodies).toReadOnly();
      const bodyIdx = shaderDrivenInstancing
        ? attribute('bodyIndex', 'uint')
        : uint((instancedBufferAttribute(bodyIndexAttr, 'vec4') as any).x);
      const pos = positions.element(bodyIdx).xyz;
      const quat = quaternions.element(bodyIdx);
      const qv = quat.xyz;
      const qw = quat.w;

      const rotateByQuat = (v: any) => {
        const t = qv.cross(v).mul(2.0);
        return v.add(t.mul(qw)).add(qv.cross(t));
      };

      material.positionNode = rotateByQuat(positionLocal).add(pos);
      material.normalNode = varying(
        transformNormalToView(rotateByQuat(normalLocal)).normalize(),
        'vTrackedBodyNormalView',
      );
      material.needsUpdate = true;

      if (showOutline) {
        outlineMaterial.positionNode = rotateByQuat(positionLocal.mul(outlineScale)).add(pos);
        outlineMaterial.needsUpdate = true;
      }
      bodyMesh.visible = true;
      if (showOutline) outlineMesh.visible = true;
      gpuVisualsBound = true;
    },

    dispose(): void {
      scene.remove(bodyMesh);
      if (showOutline) {
        scene.remove(outlineMesh);
      }
      if (!shaderDrivenInstancing) {
        (bodyMesh as THREE.InstancedMesh).dispose();
        (outlineMesh as THREE.InstancedMesh).dispose();
      }
      shaderGeometry?.dispose();
      material.dispose();
      outlineMaterial.dispose();
      if (ownsGeometry) geometry.dispose();
    },
  };
}
